/* @flow */
import * as React from 'react';
import { Container } from 'flux/utils';
import { isEqual } from 'lodash';

import Chat, { MAX_PARTICIPANTS_REPLIES_ENABLE } from 'entities/Chat';
import CurrentUser from 'auth/CurrentUser';
import Glyph from 'components/Glyph';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import MessageActions from 'groups/actions/MessageActions';
import Messages from 'collections/Messages';
import MessageStore from 'groups/stores/MessageStore';
import Text from 'components/Text';
import Timestamp from 'components/Timestamp';
import { mountModal } from 'lib/modal';
import { getGroupKey, EVERYONE } from 'utils/roleHelpers';

import ScheduledAnnouncementsDialog from './ScheduledAnnouncementsDialog';
import type { OrganizationTarget, GroupTarget } from 'entities/Message';

import './styles/ComposerAlert.scss';

type Props = {
  threadUuid?: ?string,
  chat: Chat,
  groupId: Backbone$Id,
  target: GroupTarget | OrganizationTarget,
  openSideBar: Function,
};

type State = {
  messagesScheduled: Messages,
};

class ComposerAlert extends React.Component<Props, State> {
  static getStores() {
    return [MessageStore];
  }

  static calculateState(prevState, props) {
    const { groupId, target } = props;
    const role = target ? target.role : EVERYONE;

    if (target && target.type === 'group_target' && target.group_id) {
      return {
        messagesScheduled: MessageStore.getMessagesScheduled(getGroupKey(target.group_id, role)),
      };
    }
    if (groupId) {
      return {
        messagesScheduled: MessageStore.getMessagesScheduled(getGroupKey(groupId, role)),
      };
    }

    return {};
  }

  constructor(props) {
    super(props);
    this.state = {
      messagesScheduled: new Messages(),
    };
    const { target, threadUuid } = props;
    if (!threadUuid) {
      return;
    }
    if (target && target.type === 'group_target' && target.group_id) {
      MessageActions.loadScheduledThreadMessages(target.group_id, threadUuid, target.role);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const currTarget = this.props.target;
    const currThreadUuid = this.props.threadUuid;
    if (
      !isEqual(prevProps.target, this.props.target) &&
      currTarget &&
      currTarget.type === 'group_target' &&
      currTarget.group_id &&
      currThreadUuid
    ) {
      MessageActions.loadScheduledThreadMessages(
        currTarget.group_id,
        currThreadUuid,
        currTarget.role
      );
    }
  }

  openScheduledMessagesDialog() {
    const { messagesScheduled } = this.state;
    mountModal(
      <ScheduledAnnouncementsDialog
        messagesScheduled={messagesScheduled}
        groupId={this.props.groupId}
        target={this.props.target}
      />,
      this
    );
  }

  renderOfficeHoursNotice() {
    const { chat } = this.props;

    if (!chat || chat.isUnsubscribed() || !chat.isPresent() || chat.getLeftMemberships()[0]) {
      return null;
    }

    let message;
    if (chat.isOutsideOfOtherOfficeHours()) {
      message = (
        <div>
          <Glyph name="clock" />
          It’s outside of office hours.
        </div>
      );
    } else if (
      !CurrentUser.getUser()
        .getOfficeHours()
        .isAvailableNow()
    ) {
      message = (
        <Link color="pitch" to="/settings/chat">
          <Glyph name="clock" />
          It’s outside of your office hours.
        </Link>
      );
    }
    if (message) {
      return <div className="ComposerAlert ComposerAlert-officeHours">{message}</div>;
    }
    return null;
  }
  renderRepliesDisabledNotice() {
    const { chat } = this.props;
    let message = 'Replies are turned off';
    if (chat.getMemberships().length > MAX_PARTICIPANTS_REPLIES_ENABLE) {
      message = `Replies are turned off for group conversations with more than ${MAX_PARTICIPANTS_REPLIES_ENABLE} participants.`;
    }
    return (
      <div
        className="ComposerAlert ComposerAlert-repliesDisabled"
        onClick={this.props.openSideBar}
        data-test-selector="composer-alert-replies-disabled">
        <Glyph name="chat-close-bubble-fill" size="large" />
        {message}
      </div>
    );
  }

  renderInsufficientlyConnectedNotice() {
    const message =
      'Messaging isn’t available. Only class owners can re-enable conversations within the class.';
    return (
      <div
        data-test-selector="composer-alert-insufficiently-connected"
        className="ComposerAlert ComposerAlert-insufficientlyConnected">
        <Glyph name="chat-mute" size="large" color="thunder" />
        <Text weight="normal" color="thunder">
          {message}
        </Text>
      </div>
    );
  }

  renderUserMutedNotice() {
    const currentUser = CurrentUser.getUser();
    const mutedUntil = currentUser.getMutedUntil();
    const timestamp = mutedUntil ? <Timestamp date={mutedUntil} format="long" /> : null;
    return (
      <div className="ComposerAlert">
        <Glyph name="chat-close-bubble-fill" size="large" />
        Your messaging privileges have been suspended
        {timestamp && <React.Fragment> until {timestamp}</React.Fragment>}.{' '}
        <Link color="blue" href={LinkConstants.COMMUNITY_GUIDELINES} target="_blank">
          More Information
        </Link>
        .
      </div>
    );
  }
  renderMembersMutedNotice() {
    const { chat } = this.props;
    const otherMember = chat.getOtherMember();
    const name =
      otherMember && otherMember.id !== -1
        ? otherMember.name
        : 'Some participants in this conversation';
    const message = `${name} may not be able to respond to your message right now.`;
    return <div className="ComposerAlert">{message}</div>;
  }
  renderScheduledMessagesNotice() {
    const numScheduled = this.state.messagesScheduled.length;
    const noticeText =
      numScheduled > 1 ? numScheduled + ' scheduled announcements' : '1 scheduled announcement';
    return (
      <div
        className="ComposerAlert ComposerAlert-scheduledMessages"
        onClick={() => {
          this.openScheduledMessagesDialog();
        }}>
        <Glyph name="schedule" data-test-selector="composer-alert-scheduled-messages" />
        {noticeText}
      </div>
    );
  }
  render() {
    const { chat, groupId, target } = this.props;
    const isGroupAnnouncementThread = groupId || (target && target.type === 'group_target');
    if (CurrentUser.getUser().isMuted()) {
      return this.renderUserMutedNotice();
    }
    if (chat) {
      // NOTE: Should be repliesDisabled() but there may be a backend bug right now
      if (chat.isDisabled()) {
        return this.renderRepliesDisabledNotice();
      } else if (chat.isInsufficientlyConnected()) {
        return this.renderInsufficientlyConnectedNotice();
      } else if (chat.hasMutedMembers()) {
        return this.renderMembersMutedNotice();
      } else {
        return this.renderOfficeHoursNotice();
      }
    }
    if (isGroupAnnouncementThread && this.state.messagesScheduled.length > 0) {
      return this.renderScheduledMessagesNotice();
    }
    return null;
  }
}
export default Container.create(ComposerAlert, {
  withProps: true,
  pure: false,
});
